import { useViewModel } from '@descomplica/microfrontend-react';
import { observer } from 'mobx-react';
import React from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '@/shared/presentation/components/ui/Button';
import { Input } from '@/shared/presentation/components/ui/Input';
import { Space } from '@/shared/presentation/components/ui/Space';

import LogoSvg from '../../assets/logo.svg';
import { ErrorMessage } from '../../components/ErrorMessage';
import { InputPassword } from '../../components/InputPassword';
import { FooterReCaptcha } from '../../components/Recaptcha';
import { ForgotPass } from './components/forget-pass-flow/ForgotPass';
import { SignInViewModel } from './SignIn.vm';

const Container = styled.div`
  max-width: 100vw;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: var(--gray-02);

  @media screen and (max-width: 480px) {
    background-color: white;

    button:first-child {
      width: 100%;
    }
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  background-color: var(--white);
  border-radius: 16px;
  color: #000;
  padding: 48px;

  @media screen and (max-width: 480px) {
    padding: 24px;
    height: 100vh;
    border-radius: 0;
    max-width: calc(100vw - 48px);
  }
`;

const Logo = styled.img.attrs({
  src: `${LogoSvg}`,
})`
  width: auto;
  max-width: 128px;
  margin: 0 auto 24px;
  display: block;
`;

const Form = styled.form`
  display: block;
`;

const Title = styled.h1`
  font-size: 32px;
  line-height: 1.2;
  color: var(--gray-90);
  margin-bottom: 8px;
`;

const Subtitle = styled.div`
  font-size: 16px;
  line-height: 20px;
  color: #636769;
  font-weight: normal;
  margin-bottom: 8px;
`;

const ForgotPassword = styled.a`
  line-height: 20px;
  color: var(--gray-90);
  font-weight: normal;
  margin-top: 16px;
  text-decoration: underline;
  display: block;
  cursor: pointer;
`;

const AlreadyHaveAccount = styled.div`
  line-height: 20px;
  color: var(--gray-90);
  font-weight: normal;
  margin-top: 16px;
  text-decoration: underline;
  display: block;
  cursor: pointer;
  text-align: center;
`;

export const SignInScreen = observer(() => {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const vm = useViewModel(SignInViewModel, { navigate, executeRecaptcha });

  const signIn = (e) => {
    e.preventDefault();
    vm.signIn();
  };

  return (
    <Container>
      <Content>
        <Form onSubmit={signIn}>
          <Logo />

          <Title>Entrar</Title>
          <Subtitle>Entre com sua conta no Descomplica.IO</Subtitle>

          {!!vm.errorMessage && (
            <ErrorMessage message={vm.errorMessage} withHelpCenterLink={vm.withHelpCenterLink} />
          )}

          <Space height={16} />

          <Input
            placeholder="E-mail"
            label="E-mail"
            name="email"
            errorMessage={vm.form.errors.email && vm.form.errors.email[0]}
            onChange={(value) => vm.form.setValue('email', value)}
            onBlur={() => vm.form.validateField('email')}
            autoFocus
            required
          />

          <Space height={16} />

          <InputPassword
            placeholder="Senha"
            label="Senha"
            type="password"
            name="password"
            errorMessage={vm.form.errors.password && vm.form.errors.password[0]}
            onChange={(value) => {
              vm.form.setValue('password', value);
            }}
            onBlur={() => vm.form.validateField('password')}
            required
          />

          <ForgotPassword
            aria-label="Botão de esqueceu a senha"
            onClick={() => vm.setIsForgotPasswordModalOpen(true)}
          >
            Esqueceu sua senha?
          </ForgotPassword>

          <Space height={16} />

          <Button
            fullWidth
            loading={vm.isSubmitting}
            disabled={!vm.form.isValid || vm.isSubmitting || !executeRecaptcha}
            variant="primary"
            type="submit"
            aria-disabled={!vm.form.isValid || vm.isSubmitting || !executeRecaptcha}
            aria-label="Botão para Entrar"
          >
            Entrar
          </Button>

          {vm.enabledSignUp && (
            <AlreadyHaveAccount
              aria-label="Botão de ir para a tela de cadastro"
              onClick={() => vm.goToSignUp()}
            >
              Não tem uma conta? Cadastre-se
            </AlreadyHaveAccount>
          )}

          <FooterReCaptcha />
        </Form>
      </Content>

      {vm.isForgotPasswordModalOpen && (
        <ForgotPass onClose={() => vm.setIsForgotPasswordModalOpen(false)} />
      )}
    </Container>
  );
});
